import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, FileCheck2, PenLine, ShieldAlert } from "lucide-react";
import { useWizard } from "@/context/WizardContext";
import { TextInput, SectionHeading, Grid } from "@/components/wizard/FormKit";
import { RN_CHECKLIST_ITEMS } from "@/lib/sampleData";
import { fireConfetti } from "@/lib/celebrate";
import { checklistBlockers } from "@/lib/dhsRules";
import PreSubmitPreview from "@/components/wizard/PreSubmitPreview";
import RecertDiffCard from "@/components/RecertDiffCard";
import SignaturePadField from "@/components/wizard/SignaturePadField";
import { completeAvidaDraft, submitStandaloneIntake } from "@/lib/avidaDraft";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SignatureBlock — finger-drawn signature pad + printed name + date + role-extras.
 *
 * The drawn PNG is the legally captured signature; the printed name and date
 * are also kept because the official Wisconsin DHS forms have separate fields
 * for "SIGNATURE" and "PRINTED NAME / DATE".
 */
function SignatureBlock({
  label, role, testidPrefix,
  nameValue, dateValue, extraLabel, extraValue, signatureValue,
  onName, onDate, onExtra, onSignature,
  signatureRole,
}) {
  return (
    <div className="rounded-xl border border-[#D6E3EF] p-5 bg-white space-y-4" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center gap-2">
        <PenLine className="w-4 h-4 text-[#0A3D79]" />
        <h4 className="font-display text-[16px] font-semibold text-[#0B1E3A]">{label}</h4>
      </div>
      <p className="text-[13px] text-[#3A5275]">{role}</p>
      <SignaturePadField
        role={signatureRole}
        label="Sign with finger or stylus"
        testid={`${testidPrefix}-pad`}
        value={signatureValue}
        onChange={onSignature}
        required
      />
      <Grid cols={extraLabel ? 3 : 2}>
        <TextInput label="Printed Name" required
          value={nameValue} onChange={onName} testid={`${testidPrefix}-name`} />
        <TextInput label="Date" type="date" required
          value={dateValue} onChange={onDate} testid={`${testidPrefix}-date`} />
        {extraLabel && (
          <TextInput label={extraLabel}
            value={extraValue} onChange={onExtra} testid={`${testidPrefix}-extra`} />
        )}
      </Grid>
    </div>
  );
}

export default function Step9Verify() {
  const { data, update, setData } = useWizard();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // DHS blockers → any checklist item with ≥1 unresolved DHS error is locked.
  const blockers = useMemo(() => checklistBlockers(data), [data]);

  // Auto-uncheck any item that becomes DHS-blocked (compliance cannot be faked).
  useEffect(() => {
    const toClear = {};
    for (const id of Object.keys(blockers)) {
      if (data.rn_checklist[id]) toClear[id] = false;
    }
    if (Object.keys(toClear).length) {
      update("rn_checklist", toClear);
      toast.warning(
        `DHS compliance issue detected — ${Object.keys(toClear).length} checklist item(s) auto-unchecked until resolved.`,
        { duration: 5000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockers]);

  const checklistDone = useMemo(
    () => RN_CHECKLIST_ITEMS.every((i) => data.rn_checklist[i.id]),
    [data.rn_checklist]
  );
  const checklistCount = RN_CHECKLIST_ITEMS.filter((i) => data.rn_checklist[i.id]).length;
  const blockedCount = Object.keys(blockers).length;

  const sig = data.signatures;
  const aiOk = data.ai_review && data.ai_review.ready_to_submit;
  const rnOk = !!sig.rn_name && !!sig.rn_date && !!sig.rn_signature_png;
  const repOk = !!sig.agency_rep_name && !!sig.agency_rep_date && !!sig.agency_rep_signature_png;
  const clientOk = !!sig.client_signature_png;
  const distinct = sig.rn_name && sig.agency_rep_name &&
    sig.rn_name.trim().toLowerCase() !== sig.agency_rep_name.trim().toLowerCase();
  const canSubmit = checklistDone && rnOk && repOk && clientOk && distinct && blockedCount === 0;

  const toggle = (id) => {
    if (blockers[id]) {
      toast.error(
        `DHS: ${blockers[id][0]?.label || blockers[id][0]?.message || "Resolve the rule violation before checking this item."}`,
        { duration: 5000 }
      );
      return;
    }
    update("rn_checklist", { [id]: !data.rn_checklist[id] });
  };

  const generate = async () => {
    if (!canSubmit) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      toast.error("You're offline — reconnect before generating PDFs. Your draft is safe on this device.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/wizard/generate`, data, { timeout: 60000 });
      fireConfetti();
      toast.success("All 5 PDFs generated ✨");

      // Stage 3 outbound handoff — if this wizard session was started from a
      // parent-app visit context, ship the completed packet back to the
      // parent's webhook now. We don't await this on the navigation path
      // because the PDFs are already downloadable; handoff result will be
      // shown on the Download Center via the handoff-status endpoint.
      let activeVisit = null;
      try {
        const raw = localStorage.getItem("wi-pa-active-visit-v1");
        if (raw) activeVisit = JSON.parse(raw);
      } catch { /* non-fatal */ }
      if (activeVisit?.visit_id) {
        // Drop the mid-flight checkpoint — submission is final, server-side
        // resume is no longer relevant.
        axios.delete(`${API}/visits/${activeVisit.visit_id}/draft`).catch(() => {});

        axios.post(`${API}/wizard/${res.data.job_id}/handoff`, {
          visit_id: activeVisit.visit_id,
        }, { timeout: 60000 })
          .then((h) => {
            if (h.data?.delivered) {
              toast.success("Handed off to parent app ✓");
            } else {
              toast.error(`Handoff retrying — last error: ${h.data?.attempts?.slice(-1)[0]?.error || h.data?.attempts?.slice(-1)[0]?.status_code || "unknown"}`);
            }
          })
          .catch((he) => {
            toast.error(`Handoff failed: ${he.response?.data?.detail || he.message}`);
          })
          .finally(() => {
            try { localStorage.removeItem("wi-pa-active-visit-v1"); } catch { /* non-fatal */ }
          });
      }

      // Prompt 6B — if this wizard session was seeded from an Avida Care
      // coordinator deep-link, notify Avida that the assessment is
      // complete. Fire-and-forget (retry chain + admin alerts run server-
      // side); the RN is not blocked by the outbound webhook.
      let avidaDraftId = "";
      try { avidaDraftId = sessionStorage.getItem("wi-pa-avida-draft-id-v1") || ""; }
      catch { /* non-fatal */ }
      if (avidaDraftId) {
        completeAvidaDraft(avidaDraftId, res.data.job_id)
          .then((ack) => {
            if (ack?.queued) {
              toast.success("Notifying Avida Care…", {
                description: "Documents + PA summary queued for delivery.",
              });
            }
          })
          .catch((ae) => {
            console.warn("[avida] completeAvidaDraft threw:", ae);
          });
      }

      // Phase 6J — standalone (orphan) intake path. When the wizard
      // was started without an Avida assignment (Landing's "Start
      // standalone intake" link), POST the full payload to the orphan
      // endpoint so a coordinator can reconcile it later. On submit
      // failure, the helper queues to localStorage; the RN's PDFs are
      // still generated and downloadable regardless of sync status.
      let standaloneMode = false;
      try { standaloneMode = sessionStorage.getItem("wi-pa-standalone-mode-v1") === "1"; }
      catch { /* non-fatal */ }
      if (standaloneMode && !avidaDraftId) {
        submitStandaloneIntake({
          jobId: res.data.job_id,
          rnUserId: "",
          wizardState: data,
          capturedAt: new Date().toISOString(),
        }).then((ack) => {
          if (ack?.ok) {
            toast.success("Standalone intake saved", {
              description: "A coordinator will reconcile it to a parent-app pipeline.",
            });
          } else if (ack?.queued) {
            toast.warning("Saved offline — will sync when online", {
              description: "Your PDFs are ready now; the standalone record is queued.",
            });
          }
        }).catch((se) => {
          console.warn("[standalone] submit threw:", se);
        });
        try { sessionStorage.removeItem("wi-pa-standalone-mode-v1"); }
        catch { /* non-fatal */ }
      }

      navigate(`/download/${res.data.job_id}`, { state: res.data });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Generation blocked: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-10">
      <RecertDiffCard />
      <PreSubmitPreview />
      <section className="card-surface fade-up">
        <SectionHeading
          eyebrow={`Step 9 · Triple Verification (2 of 3) — ${checklistCount}/${RN_CHECKLIST_ITEMS.length} complete`}
          title="RN Verification Checklist"
          description="Every item must be individually confirmed by the Registered Nurse before PDFs can be generated."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {RN_CHECKLIST_ITEMS.map((item) => {
            const checked = !!data.rn_checklist[item.id];
            const blocks = blockers[item.id];
            const isBlocked = !!blocks;
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => toggle(item.id)}
                data-testid={`checklist-${item.id}`}
                data-blocked={isBlocked ? "dhs" : undefined}
                className={`text-left rounded-xl border px-4 py-3 flex items-start gap-3 transition ${
                  isBlocked
                    ? "bg-[#fbeae5] border-[#e2b6a9] cursor-not-allowed"
                    : checked
                      ? "bg-[#E7F1FB] border-[#1FA0E6]"
                      : "bg-white border-[#D6E3EF] hover:border-[#1FA0E6]"
                }`}
              >
                <div className={`w-6 h-6 mt-0.5 rounded-md flex items-center justify-center flex-shrink-0 border ${
                  isBlocked
                    ? "bg-[#fbeae5] border-[#c05746] text-[#c05746]"
                    : checked
                      ? "bg-[#0A3D79] border-[#0A3D79] text-[#F7FAFC]"
                      : "bg-white border-[#B7CADA]"
                }`}>
                  {isBlocked ? <ShieldAlert className="w-4 h-4" strokeWidth={2} />
                   : checked ? <Check className="w-4 h-4" strokeWidth={2.5} />
                   : null}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[14px] text-[#0B1E3A] leading-snug">{item.label}</span>
                  {isBlocked && (
                    <div className="mt-1.5 space-y-1" data-testid={`checklist-${item.id}-dhs-block`}>
                      {blocks.slice(0, 3).map((b, bi) => (
                        <div
                          key={`${b.rule || "x"}-${b.field || bi}`}
                          className="text-[11.5px] text-[#8a2e1a] leading-snug flex items-start gap-1"
                        >
                          <span className="font-mono font-semibold bg-[#0f3b5f] text-white px-1 rounded text-[10px] mt-[1px]">
                            {b.rule}
                          </span>
                          <span>{b.label || b.message}</span>
                        </div>
                      ))}
                      {blocks.length > 3 && (
                        <div className="text-[11px] text-[#8a2e1a]">+{blocks.length - 3} more…</div>
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading
          eyebrow="Step 9 · Triple Verification (3 of 3)"
          title="Signatures — RN, Agency Rep & Client"
          description="Three captures: RN attests clinically (F-11136); the Agency Authorized Representative attests administratively (F-11134) and must be a different person; the client signs the Care Plan in-home. Lost signatures cannot be re-captured for ~60 days, so each pad auto-saves after every stroke and is mirrored to a backup that survives wizard resets."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SignatureBlock
            label="Registered Nurse — F-11136"
            role="Clinical attestation of F-11136 Personal Care Addendum."
            testidPrefix="sig-rn"
            signatureRole="rn"
            signatureValue={sig.rn_signature_png}
            onSignature={(v) => update("signatures", { rn_signature_png: v })}
            nameValue={sig.rn_name} onName={(v) => update("signatures", { rn_name: v })}
            dateValue={sig.rn_date} onDate={(v) => update("signatures", { rn_date: v })}
            extraLabel="RN License #"
            extraValue={sig.rn_license} onExtra={(v) => update("signatures", { rn_license: v })}
          />
          <SignatureBlock
            label="Agency Authorized Representative — F-11134"
            role="Administrative attestation of F-11134 Provider Acknowledgement (must be a different person from the RN)."
            testidPrefix="sig-rep"
            signatureRole="agency_rep"
            signatureValue={sig.agency_rep_signature_png}
            onSignature={(v) => update("signatures", { agency_rep_signature_png: v })}
            nameValue={sig.agency_rep_name} onName={(v) => update("signatures", { agency_rep_name: v })}
            dateValue={sig.agency_rep_date} onDate={(v) => update("signatures", { agency_rep_date: v })}
            extraLabel="Title"
            extraValue={sig.agency_rep_title} onExtra={(v) => update("signatures", { agency_rep_title: v })}
          />
        </div>

        <div className="mt-6">
          <SignatureBlock
            label="Client / Member — Care Plan"
            role="Client acknowledgement of the proposed Personal Care Plan during the in-home visit. Captured once; cannot be re-collected for ~60 days."
            testidPrefix="sig-client"
            signatureRole="client"
            signatureValue={sig.client_signature_png}
            onSignature={(v) => update("signatures", { client_signature_png: v })}
            nameValue={sig.client_name} onName={(v) => update("signatures", { client_name: v })}
            dateValue={sig.client_sign_date} onDate={(v) => update("signatures", { client_sign_date: v })}
          />
        </div>

        {sig.rn_name && sig.agency_rep_name && !distinct && (
          <div className="mt-4 px-4 py-3 rounded-lg bg-[#fbeae5] border border-[#e2b6a9] text-[#c05746] text-[14px]" data-testid="sig-violation">
            Dual-signature violation: the RN and Agency Representative must be different people.
          </div>
        )}
      </section>

      <section className="card-surface fade-up">
        <h3 className="font-display text-lg font-semibold text-[#0B1E3A] mb-4">Verification Gate</h3>
        <ul className="space-y-2 text-[14px] mb-6">
          <li className="flex items-center gap-2" data-testid="gate-dhs">
            <div className={`w-5 h-5 rounded-full ${blockedCount === 0 ? "bg-[#0A3D79]" : "bg-[#c05746]"} flex items-center justify-center`}>
              {blockedCount === 0
                ? <Check className="w-3 h-3 text-[#F7FAFC]" strokeWidth={3} />
                : <ShieldAlert className="w-3 h-3 text-[#F7FAFC]" strokeWidth={3} />}
            </div>
            <span className={blockedCount === 0 ? "text-[#0B1E3A]" : "text-[#c05746]"}>
              {blockedCount === 0
                ? "Wisconsin DHS F-11133 compliance passed"
                : `${blockedCount} DHS rule violation(s) blocking ${blockedCount} checklist item(s)`}
            </span>
          </li>
          <li className="flex items-center gap-2" data-testid="gate-ai">
            <div className={`w-5 h-5 rounded-full ${aiOk ? "bg-[#0A3D79]" : "bg-[#B7CADA]"} flex items-center justify-center`}>
              {aiOk && <Check className="w-3 h-3 text-[#F7FAFC]" strokeWidth={3} />}
            </div>
            <span className={aiOk ? "text-[#0B1E3A]" : "text-[#3A5275]"}>AI review passed ({aiOk ? "ready" : data.ai_review ? "issues remain" : "not run"})</span>
          </li>
          <li className="flex items-center gap-2" data-testid="gate-checklist">
            <div className={`w-5 h-5 rounded-full ${checklistDone ? "bg-[#0A3D79]" : "bg-[#B7CADA]"} flex items-center justify-center`}>
              {checklistDone && <Check className="w-3 h-3 text-[#F7FAFC]" strokeWidth={3} />}
            </div>
            <span className={checklistDone ? "text-[#0B1E3A]" : "text-[#3A5275]"}>RN checklist {checklistCount}/{RN_CHECKLIST_ITEMS.length}</span>
          </li>
          <li className="flex items-center gap-2" data-testid="gate-sigs">
            <div className={`w-5 h-5 rounded-full ${rnOk && repOk && distinct ? "bg-[#0A3D79]" : "bg-[#B7CADA]"} flex items-center justify-center`}>
              {rnOk && repOk && distinct && <Check className="w-3 h-3 text-[#F7FAFC]" strokeWidth={3} />}
            </div>
            <span className={rnOk && repOk && distinct ? "text-[#0B1E3A]" : "text-[#3A5275]"}>RN + Agency Rep finger-signed, dated, and distinct</span>
          </li>
          <li className="flex items-center gap-2" data-testid="gate-client-sig">
            <div className={`w-5 h-5 rounded-full ${clientOk ? "bg-[#0A3D79]" : "bg-[#B7CADA]"} flex items-center justify-center`}>
              {clientOk && <Check className="w-3 h-3 text-[#F7FAFC]" strokeWidth={3} />}
            </div>
            <span className={clientOk ? "text-[#0B1E3A]" : "text-[#3A5275]"}>Client signed Care Plan in-home</span>
          </li>
        </ul>

        <button
          type="button"
          onClick={generate}
          disabled={!canSubmit || submitting}
          className="btn-primary"
          data-testid="generate-pdfs-btn"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating 5 PDFs…</> : <><FileCheck2 className="w-4 h-4" /> Generate All 5 PDFs</>}
        </button>
        <p className="text-[12.5px] text-[#3A5275] mt-3">
          Note: AI review is advisory. Wisconsin DHS rules, RN checklist, and dual distinct signatures are <span className="font-semibold">hard gates</span> enforced on the server.
        </p>
      </section>
    </div>
  );
}
